import { api } from '../../../common/api/httpClient';
import type {
  PortalOperationFavoriteState,
  PortalOperationPageResponse,
  PortalOperationResponse,
} from '../types/portal.types';

export type OperationQuery = {
  page?: number;
  pageSize?: number;
  keyword?: string;
  groupCode?: string;
  favoriteOnly?: boolean;
  availableOnly?: boolean;
};

function queryString(query: OperationQuery) {
  const values = new URLSearchParams();
  if (query.page) values.set('page', String(query.page));
  if (query.pageSize) values.set('pageSize', String(query.pageSize));
  if (query.keyword?.trim()) values.set('keyword', query.keyword.trim());
  if (query.groupCode) values.set('groupCode', query.groupCode);
  if (query.favoriteOnly) values.set('favoriteOnly', 'true');
  if (query.availableOnly) values.set('availableOnly', 'true');
  const encoded = values.toString();
  return encoded ? `?${encoded}` : '';
}

function mergePages(
  pages: PortalOperationPageResponse[],
): PortalOperationPageResponse {
  const first = pages[0];
  if (!first) {
    return {
      total: 0,
      page: 1,
      pageSize: 100,
      favoriteVersion: 0,
      groupFilters: [],
      groups: [],
    };
  }
  const groups = new Map(
    first.groups.map(group => [group.groupCode, {
      ...group,
      operations: [...group.operations],
    }]),
  );
  pages.slice(1).flatMap(page => page.groups)
    .forEach(group => {
      const existing = groups.get(group.groupCode);
      groups.set(group.groupCode, existing ? {
        ...existing,
        operations: [
          ...existing.operations,
          ...group.operations,
        ],
      } : group);
    });
  return {
    ...first,
    page: 1,
    groups: [...groups.values()],
  };
}

export const operationsApi = {
  list(query: OperationQuery = {}) {
    return api<PortalOperationPageResponse>(
      `/api/portal/operations${queryString(query)}`,
    );
  },

  async listAll(query: Omit<
    OperationQuery, 'page' | 'pageSize'
  > = {}) {
    const pageSize = 100;
    const first = await operationsApi.list({
      ...query, page: 1, pageSize,
    });
    const pages = [first];
    const pageCount = Math.ceil(first.total / first.pageSize);
    for (let page = 2; page <= pageCount; page += 1) {
      pages.push(await operationsApi.list({
        ...query, page, pageSize,
      }));
    }
    return mergePages(pages);
  },

  detail(applicationCode: string, operationCode: string) {
    return api<PortalOperationResponse>(
      `/api/portal/operations/${encodeURIComponent(applicationCode)}/${encodeURIComponent(operationCode)}`,
    );
  },

  favorites() {
    return api<PortalOperationFavoriteState>(
      '/api/portal/operation-favorites',
    );
  },

  replaceFavorites(state: PortalOperationFavoriteState) {
    return api<PortalOperationFavoriteState>(
      '/api/portal/operation-favorites',
      { method: 'PUT', body: JSON.stringify(state) },
    );
  },
};
